import {
  AccountResponse,
  ChargeResponse,
  ChargeState,
  EnterprisePlanInvoiceResponse,
  PlanInfo,
  ProjectFlagResponse,
} from "../../types";

const DAYS_PER_MONTH = 365 / 12;
const MILLISECONDS_IN_DAY = 1000 * 60 * 60 * 24;

// The name of the project flag that opts a project out of AI features.
const AI_OPTED_OUT_FLAG_NAME = "is_ai_opted_out";

// The name of the project flag that gates Field Capture.
const FIELD_CAPTURE_FLAG_NAME = "is_field_captures_enabled";

interface SelectChoice {
  id: string;
  name: string;
}

const componentUtils = {
  calculateMrrForEnterprisePlanInvoice(
    enterprisePlanInvoice: EnterprisePlanInvoiceResponse,
  ): number {
    if (!(enterprisePlanInvoice.start_at && enterprisePlanInvoice.end_at)) {
      return 0;
    }

    // prettier-ignore
    const endAtStartOfDay = new Date(enterprisePlanInvoice.end_at).setUTCHours(0, 0, 0, 0);
    // prettier-ignore
    const startAtStartOfDay = new Date(enterprisePlanInvoice.start_at).setUTCHours(0, 0, 0, 0);

    // prettier-ignore
    const differenceInCalendarDays = (endAtStartOfDay - startAtStartOfDay) / MILLISECONDS_IN_DAY;

    // prettier-ignore
    return (DAYS_PER_MONTH * enterprisePlanInvoice.amount) / differenceInCalendarDays;
  },

  /**
   * Transform an `enum` into an array of `{id: string, name: string }`
   * objects for consumption by `SelectInput`s, where name is capital case.
   *
   * ```
   *  Input: enum { AccountMember = "account_member", AccountAdmin = "account_admin" }
   *  Output: [ { id: "account_member", name: "Account Member" }, { id: "account_admin", name: "Account Admin" } ]
   * ```
   *
   * Please note if non `snake_case` strings, `fooBar` for example,
   * are used in the `enum`, the function will behave unexpectedly!
   */
  generateCapitalCaseSelectChoicesFromEnum: (e: any): SelectChoice[] => {
    return generateSelectChoicesFromEnum(e, snakeCaseToCapitalCase);
  },

  /**
   * Transform an `enum` into an array of `{id: string, name: string }`
   * objects for consumption by `SelectInput`s, where name is upper case.
   *
   * ```
   *  Input: enum { Eu = "eu", Us = "us" }
   *  Output: [ { id: "eu", name: "EU" }, { id: "us", name: "US" } ]
   * ```
   *
   * Please note if non `snake_case` strings, `fooBar` for example,
   * are used in the `enum`, the function will behave unexpectedly!
   */
  generateUpperCaseSelectChoicesFromEnum: (e: any): SelectChoice[] => {
    return generateSelectChoicesFromEnum(e, snakeCaseToUpperCase);
  },

  /**
   * Transform regions array into an array of `{id: string, name: string }`
   * ```
   *  Input: ["eu", "us"]
   *  Output: [ { id: "eu", name: "EU" }, { id: "us", name: "US" } ]
   * ```
   */
  generateSelectChoicesFromRegions: (regions: string[]): SelectChoice[] => {
    return regions.map((region: string) => ({
      id: region,
      name: region.toUpperCase(),
    }));
  },

  /**
   * Returns true if account is on a basic (free) plan. At this
   * time, the only basic (free) plan is the team v1 stripe plan.
   */
  isAccountBasic: (account: AccountResponse) => {
    return (
      account.plan_info === PlanInfo.StripeTeamV1 ||
      account.plan_info === PlanInfo.OtcPending
    );
  },

  /**
   * Returns true if charge can be retried.
   */
  isChargeRetriable: (charge: ChargeResponse) => {
    return charge.state === ChargeState.Failed;
  },

  /**
   * Returns true if one of the passed in project flags opts the project out of AI,
   * i.e. if a flag named `is_ai_opted_out` is present and its value is true.
   * A missing flag means the project has AI enabled (not opted out).
   */
  isAiOptedOut: (flags: ProjectFlagResponse[] | undefined) => {
    return (
      flags?.some(
        (flag: ProjectFlagResponse) =>
          flag.name === AI_OPTED_OUT_FLAG_NAME && flag.value,
      ) ?? false
    );
  },

  /**
   * Returns true if one of the passed in project flags enable Field Capture,
   * i.e. if a flag named `is_field_captures_enabled` is present and its value is true.
   * A missing flag means Field Capture is disabled.
   */
  isFieldCaptureEnabled: (flags: ProjectFlagResponse[] | undefined) => {
    return (
      flags?.some(
        (flag: ProjectFlagResponse) =>
          flag.name === FIELD_CAPTURE_FLAG_NAME && flag.value,
      ) ?? false
    );
  },

  /**
   * Returns true if plan info corresponds to an enterpise plan.
   */
  isPlanInfoEnterprisePlan: (planInfo: PlanInfo) => {
    return (
      planInfo === PlanInfo.EnterprisePro ||
      planInfo === PlanInfo.EnterpriseBusiness ||
      planInfo === PlanInfo.EnterpriseBusinessPlus ||
      planInfo === PlanInfo.EnterprisePremier
    );
  },

  snakeCaseToCapitalCase: (str: string): string => {
    return snakeCaseToCapitalCase(str);
  },
};

const generateSelectChoicesFromEnum = (
  e: any,
  strTransformFn: (str: string) => string,
): SelectChoice[] => {
  return Object.values(e).map((snakeCaseStr: any) => {
    return {
      id: snakeCaseStr,
      name: strTransformFn(snakeCaseStr),
    };
  });
};

const snakeCaseToCapitalCase = (str: string) => {
  return str
    .split("_")
    .map((s: string) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" ");
};

const snakeCaseToUpperCase = (str: string) => {
  return str
    .split("_")
    .map((s: string) => s.toUpperCase())
    .join(" ");
};

export { AI_OPTED_OUT_FLAG_NAME, FIELD_CAPTURE_FLAG_NAME };
export default componentUtils;
